import { useMemo } from 'react';
import { isComponentHidden } from '@shared/data/cards/utils';
import {
  isInputButtonComponent,
  isInputComponent,
  isManagingInputComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

export interface UseVisibleComponentsProps {
  workflowTemplate: API.WorkflowTemplateFragment | undefined;
  viewTemplate: API.ViewTemplateFragment | undefined;
  includeServerManagedComponents?: boolean;
  includeManagedByComponents?: boolean;
  includeInputComponentsOnly?: boolean;
  isReadOnly?: boolean;
}

export function useVisibleComponents({
  workflowTemplate,
  viewTemplate,
  includeServerManagedComponents = true,
  includeManagedByComponents = false,
  includeInputComponentsOnly = false,
  isReadOnly = false,
}: UseVisibleComponentsProps): API.AnyComponent[] {
  return useMemo(() => {
    return getVisibleComponents(workflowTemplate?.components ?? [], viewTemplate)
      .filter(component => includeServerManagedComponents || !component.isServerManaged)
      .filter(component => includeManagedByComponents || !component.managedByComponentId)
      .filter(component => {
        return (
          !includeInputComponentsOnly ||
          isInputComponent(component) ||
          isManagingInputComponent(component)
        );
      })
      .filter(component => !isReadOnly || !isHiddenWhenReadOnly(component));
  }, [
    workflowTemplate?.components,
    viewTemplate,
    includeServerManagedComponents,
    includeManagedByComponents,
    includeInputComponentsOnly,
    isReadOnly,
  ]);
}

function getVisibleComponents(
  components: API.AnyComponent[],
  viewTemplate?: API.ViewTemplateFragment,
) {
  return components.filter(component => {
    const viewOptions = viewTemplate?.componentsViewOptions?.find(
      option => option.componentId === component.id,
    );
    return !isComponentHidden(viewTemplate, viewOptions, component);
  });
}

function isHiddenWhenReadOnly(component: API.AnyComponent) {
  return isInputButtonComponent(component);
}
